/**
 * Channel-area mapper (T041, US4 — FR-005). Maps a communication channel to a project: the naming
 * convention `#{client}-{project?}-{area}` pre-suggests the area (admin confirms/corrects), and a
 * `has_client` toggle records whether the client is present (for response-time calc). Non-conforming
 * channels fall back to manual area selection. Editing requires pm/admin (server-enforced).
 */
import { useEffect, useState } from 'react';
import type { ChannelArea } from '../types/entities';
import { guessArea, upsertChannel } from '../services/channels';

const AREA_OPTIONS: ChannelArea[] = ['sales', 'ops', 'pm'];

export function ChannelMapper({ projectId, canEdit }: { projectId: string; canEdit: boolean }) {
  const [channelName, setChannelName] = useState('');
  const [channelId, setChannelId] = useState('');
  const [area, setArea] = useState<ChannelArea | ''>('');
  const [areaTouched, setAreaTouched] = useState(false);
  const [hasClient, setHasClient] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Live convention hint: pre-fill the area from the name until the admin overrides it.
  const suggested = guessArea(channelName);
  useEffect(() => {
    if (!areaTouched) setArea(suggested ?? '');
  }, [suggested, areaTouched]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await upsertChannel({
        kind: 'channel',
        project_id: projectId,
        external_id: channelId.trim() || channelName.trim(),
        channel_name: channelName.trim(),
        area: area || null,
        has_client: hasClient,
      });
      const w = res.warnings.length ? ` · ${res.warnings.join('; ')}` : '';
      setResult(
        `Channel mapped → area ${res.doc.area ?? '—'} · convention_suggested=${String(
          res.doc.convention_suggested,
        )}${w}`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Channel mapping failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <fieldset disabled={!canEdit || busy} style={fs}>
      <legend>Map a communication channel</legend>
      <label style={row}>
        <span style={lbl}>Channel name</span>
        <input
          placeholder="#arguilea-ops"
          value={channelName}
          onChange={(e) => setChannelName(e.target.value)}
          style={{ flex: 1 }}
        />
      </label>
      <label style={row}>
        <span style={lbl}>Channel id (optional)</span>
        <input placeholder="C0123ABC" value={channelId} onChange={(e) => setChannelId(e.target.value)} />
      </label>
      <label style={row}>
        <span style={lbl}>Area {suggested && !areaTouched ? `(suggested: ${suggested})` : ''}</span>
        <select
          value={area}
          onChange={(e) => {
            setArea(e.target.value as ChannelArea | '');
            setAreaTouched(true);
          }}
        >
          <option value="">— select —</option>
          {AREA_OPTIONS.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
      </label>
      <label style={{ ...row, gap: 6 }}>
        <input type="checkbox" checked={hasClient} onChange={(e) => setHasClient(e.target.checked)} />
        <span>Client is present in this channel (has_client)</span>
      </label>

      <button type="button" onClick={() => void submit()} disabled={!channelName.trim() || !area}>
        {busy ? 'Mapping…' : 'Map channel'}
      </button>
      {result && <p style={{ color: 'green' }}>{result}</p>}
      {error && <p style={{ color: 'crimson' }}>Error: {error}</p>}
    </fieldset>
  );
}

const fs: React.CSSProperties = { border: '1px solid #ddd', padding: 12, marginTop: 12 };
const row: React.CSSProperties = { display: 'flex', gap: 8, alignItems: 'center', margin: '4px 0' };
const lbl: React.CSSProperties = { width: 200 };
